import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { icons } from '../../../assets/icons';
import ImprimerLogo from '../../../assets/icons/svg/document-text.svg';
import './index.scss';
import { useReactToPrint } from 'react-to-print';
import PrintToPdf from './PrintToPdf';
import { useDispatch } from 'react-redux';
import { getBillById, createFactureFromBill } from '../../../api/redux/slices/Admin';
import { ClientInfo, ShopInfo } from '../../../components/userInfo';
import { convertDate, roundToThreeDecimals } from '../../../helpers/modules';
import { LoaderComponent } from '../../../helpers/components/loader';
import { toast } from 'react-toastify';
import {
	billCalculation,
	convertNumberToWords,
	finalTotal,
	total,
	totalNet,
} from './billFunctions';

const BillDetails = () => {
	const params = useParams();
	const navigate = useNavigate();
	const [detailBill, setDetailBill] = useState({ bill: {}, list: [], companies: [] });
	const [isLoading, setIsLoading] = useState(false);
	const [validating, setValidating] = useState(false);
	const dispatch = useDispatch();
	const [countryMap, setCountryMap] = useState({});
	const [countryNames, setCountryNames] = useState({});

	const componentRef = useRef();
	const handlePrint = useReactToPrint({
		content: () => componentRef.current,
	});

	// Using the working world-countries API
	useEffect(() => {
		fetch('https://cdn.jsdelivr.net/npm/world-countries@4/dist/countries.json')
			.then(res => res.json())
			.then(data => {
				const map = {};
				const names = {};
				data.forEach(c => {
					if (c.cca3 && c.cca2) {
						map[c.cca3] = c.cca2.toLowerCase();
						names[c.cca3] = c.name.common;
					}
				});
				setCountryMap(map);
				setCountryNames(names);
			})
			.catch(err => console.error('Failed to fetch country codes:', err));
	}, []);

	const getFlagUrl = (cca3) => {
		if (!cca3 || !countryMap[cca3]) return null;
		return `https://hatscripts.github.io/circle-flags/flags/${countryMap[cca3]}.svg`;
	};

	const getCountryName = (cca3) => {
		return countryNames[cca3] || cca3;
	};

	useEffect(() => {
		setIsLoading(true);
		dispatch(getBillById({ token: localStorage.getItem('accesstoken'), id: params.id }))
			.unwrap()
			.then((res) => {
				setDetailBill({
					bill: { ...res.bill },
					list: [...res.list],
					companies: res.companies || [],
					clientCompany: res.clientCompany,
					clientInfo: res.clientInfo, // Add clientInfo from backend
					adminCompany: res.adminCompany,
					billTotal: res.billTotal,
				});
			})
			.finally(() => setIsLoading(false));
	}, [dispatch, params]);

	const isTunisia = !detailBill?.bill?.order_id?.shopInfo?.country || detailBill?.bill?.order_id?.shopInfo?.country === 'TUN';
	const shopCountry = detailBill?.bill?.order_id?.shopInfo?.country;
	const isClientRegistered = detailBill?.clientInfo?.is_registered === true;

	const timbreCount = detailBill?.companies?.length || 1;
	const effectiveTimbre = isTunisia ? timbreCount : 0;
	const timbreTotal = isTunisia ? timbreCount * 1 : 0;

	const { billCalculationArr, totalNetValue, totalArr, finalTotalValues } = useMemo(() => {
		const billCalculationArr = billCalculation(detailBill);
		const totalNetValue = detailBill?.billTotal != null 
			? detailBill.billTotal 
			: totalNet(detailBill, effectiveTimbre);
		const totalArr = total(detailBill, effectiveTimbre);
		const finalTotalValues = finalTotal(billCalculationArr, effectiveTimbre);
		return { billCalculationArr, totalNetValue, totalArr, finalTotalValues };
	}, [detailBill, effectiveTimbre]);

	const hasCompany = () => detailBill?.clientCompany ? true : false;
	const calculatePlatformFee = () => !hasCompany() ? 0 : totalNetValue * 0.1;
	const platformFee = calculatePlatformFee();

	const handleValidateFacture = async () => {
		setValidating(true);
		try {
			await dispatch(createFactureFromBill({ 
				token: localStorage.getItem('accesstoken'), 
				billId: detailBill.bill._id 
			})).unwrap();
			toast.success('Facture validée avec succès');
			navigate('/admin/facture/1');
		} catch (error) {
			toast.error(error?.message || 'Erreur lors de la validation');
		} finally {
			setValidating(false);
		}
	};

	if (isLoading) return <LoaderComponent />;

	return (
		<div id='billDetailsAdmin'>
			<div className='details-bill-header'>
				<Link to={`/admin/bills/${params.page}`}>
					<icons.BsArrowLeft style={{ color: '#5B5B5B', marginRight: '10px' }} />
					<h1>Factures Proformat</h1>
				</Link>
				<div style={{ display: 'flex', gap: '10px' }}>
					<button 
						onClick={handleValidateFacture} 
						disabled={validating}
						className='button-primary'
						style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
					>
						{validating ? 'Validation...' : <><icons.BsCheckCircle style={{ color:'white',fontSize: '18px' }} />Valider</>}
					</button>
					<button onClick={handlePrint} style={{ display: 'flex', gap: '10px' }} className='button-primary'>
						<img src={ImprimerLogo} alt='imprimer logo' />Imprimer
					</button>
				</div>
			</div>

			<div id='title-bill'><h1>🛒 JEUN'ESS MARKET</h1></div>

			<div className='container'>
				{/* Alert for non-registered client */}
				{!isClientRegistered && (
					<div className='platform-fee-alert' style={{ backgroundColor: '#FFF8E1', border: '2px solid #FF9800', borderRadius: '8px', padding: '15px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
						<div style={{ backgroundColor: '#FF9800', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
							<icons.BsPersonPlus style={{ color: 'white', fontSize: '20px' }} />
						</div>
						<div>
							<h3 style={{ margin: '0 0 5px 0', color: '#E65100', fontSize: '16px', fontWeight: 'bold' }}>👤 Client non inscrit</h3>
							<p style={{ margin: '0', color: '#E65100', fontSize: '14px', lineHeight: '1.5' }}>Ce client a été ajouté manuellement par l'administrateur et n'a pas de compte sur la plateforme.</p>
						</div>
					</div>
				)}

				{!isTunisia && (
					<div className='platform-fee-alert' style={{ backgroundColor: '#E0F2FE', border: '2px solid #0284C7', borderRadius: '8px', padding: '15px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
						<div style={{ backgroundColor: '#0284C7', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
							<icons.BsGlobe style={{ color: 'white', fontSize: '20px' }} />
						</div>
						<div>
							<h3 style={{ margin: '0 0 5px 0', color: '#075985', fontSize: '16px', fontWeight: 'bold' }}>🌍 Facture Internationale</h3>
							<p style={{ margin: '0', color: '#075985', fontSize: '14px', lineHeight: '1.5' }}>Cette facture est pour un shop hors Tunisie. TVA et timbre ne sont pas applicables.</p>
						</div>
					</div>
				)}

				<div className='platform-fee-alert' style={{ backgroundColor: hasCompany() ? '#FFF3CD' : '#E8F5E9', border: hasCompany() ? '2px solid #FFA000' : '2px solid #4CAF50', borderRadius: '8px', padding: '15px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
					<div style={{ backgroundColor: hasCompany() ? '#FFA000' : '#4CAF50', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '20px' }}>
						{hasCompany() ? '⚠️' : '✅'}
					</div>
					<div>
						<h3 style={{ margin: '0 0 5px 0', color: hasCompany() ? '#856404' : '#2E7D32', fontSize: '16px', fontWeight: 'bold' }}>{hasCompany() ? '📢 Frais de Plateforme Marketing' : '✅ Frais de Plateforme Marketing'}</h3>
						{hasCompany() ? (
							<p style={{ margin: '0', color: '#856404', fontSize: '14px', lineHeight: '1.5' }}>
								<strong>10% du montant total</strong> de cette facture sera prélevé pour la plateforme. <br />
								Montant net à payer : <strong>{totalNetValue} DT</strong> → Frais (10%) : <strong style={{ color: '#D32F2F', fontSize: '15px' }}>{roundToThreeDecimals(platformFee)} DT</strong>
								<br /><span style={{ fontSize: '12px', fontStyle: 'italic' }}>💡 Ces frais peuvent être récupérés sous forme de biens ou d'achats auprès de la plateforme.</span>
							</p>
						) : (
							<p style={{ margin: '0', color: '#2E7D32', fontSize: '14px', lineHeight: '1.5' }}>Aucun frais de marketing n'est appliqué. Frais de plateforme : <strong>0 DT</strong></p>
						)}
					</div>
				</div>

				<div className='credentials'>
					<div className='left'>
						<div className='left-el'><label>Numéro</label><span>{detailBill?.bill?.reference || '-'}</span></div>
						<div className='left-el'><label>Date</label><span>{detailBill?.bill?.createdAt ? convertDate(detailBill?.bill?.createdAt, 'DD-MM-YYYY') : '-'}</span></div>
						<div className='left-el'><label>Référence code client</label><span>{detailBill?.bill?.order_id?.clientInfo?.customs_code || '-'}</span></div>
						{!isClientRegistered && (
							<div className='left-el'>
								<label style={{ color: '#FF9800' }}>Statut client</label>
								<span style={{ color: '#FF9800', fontWeight: 'bold' }}>Non inscrit (ajout manuel)</span>
							</div>
						)}
					</div>
					<div className='left'>
						<div className='left-el'>
							<ClientInfo clientInfo={detailBill?.bill?.order_id?.clientInfo} />
							<ShopInfo shop={detailBill?.bill?.order_id?.shopInfo} />
							{shopCountry && (
								<div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
									{getFlagUrl(shopCountry) && <img src={getFlagUrl(shopCountry)} alt={getCountryName(shopCountry)} width="20" height="20" style={{ borderRadius: '50%' }} />}
									<span style={{ fontSize: '0.85rem', color: '#35435f', fontWeight: 600 }}>{getCountryName(shopCountry)}</span>
								</div>
							)}
						</div>
					</div>
				</div>

				<div className='devis'>
					<div className='devis-head'><h1>FACTURE Proformat</h1><span /></div>
					<div className='devis-table'>
						<table className='offres'>
							<thead>
								<tr id='titres'>
									<th>RÉFÉRENCE</th>
									<th>DÉSIGNATION</th>
									<th>QTÉ</th>
									<th>VOLUME</th>
									<th>PRIX UNIT H.T</th>
									<th>REMISE</th>
									<th>MONTANT H.T</th>
									<th>TX.TVA</th>
								</tr>
							</thead>
							<tbody>
								{detailBill?.list?.map((item, index) => (
									<tr className='elements' key={index}>
										<td>{item?.reference}</td>
										<td>{item?.designation}</td>
										<td>{item?.quantity}</td>
										<td>{item?.volume != null ? roundToThreeDecimals(item.volume) : '-'}</td>
										<td>{roundToThreeDecimals((item?.prise_product_HT * item?.quantity) / (1 - item?.remise / 100))} DT</td>
											<td>{item?.remise}%</td>
											<td>{roundToThreeDecimals(item?.prise_product_HT * item?.quantity)} DT</td>
											<td>{item?.tva}%</td>
										</tr>
								))}
							</tbody>
						</table>
					</div>

					{detailBill?.companies && detailBill.companies.length > 1 && (
						<div className='companies-section'>
							<h3>Sociétés concernées</h3>
							<div className='companies-list'>
								{detailBill.companies.map((company, idx) => (
									<div key={idx} className='company-card'>
										<div className='company-header'><strong>{company.name}</strong></div>
										<div className='company-details'>
											<p>Adresse: {company.address}</p><p>CODE TVA: {company.tva_code}</p><p>RC: {company.r_c}</p>
											<p>Code en Douane: {company.customs_code}</p><p>Tél: {company.phone}</p><p>Fax: {company.fax}</p><p>Email: {company.email}</p>
											{isTunisia && <p><strong>Timbre: 1 DT</strong></p>}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					<div className='billTable'>
						<table id='bt-principal'>
							<tbody>
								<tr id='firstBigRow'>
									<td>
										<table id='bt-nested-1'>
											<thead><tr id='bt-head-1'><th>CODE</th><th>BASE</th><th>TAUX</th><th>TOTAL TAXE</th></tr></thead>
											<tbody>
												{billCalculationArr?.map((el, index) => (
													<tr className='bt-tl-1' key={index}>
														<td>{el.CODE}</td>
														<td>{roundToThreeDecimals(el.BASE)} DT</td>
														<td>{el.TAUX}%</td>
														<td>{roundToThreeDecimals(el.TOTAL_TAXE)} DT</td>
													</tr>
												))}
												{isTunisia && <tr className='bt-tl-1'><td>Timbre</td><td>0.000 DT</td><td></td><td>{timbreTotal} DT</td></tr>}
											</tbody>
										</table>
									</td>
									<td>
										<table id='bt-nested-2'>
											<thead><tr id='bt-head-2'><th>TOTAL H.T</th><th>REMISE</th><th>ESCOMPTE</th><th>TOTAL TTC</th></tr></thead>
											<tbody>
												<tr className='bt-tl-2' id='bt-tl-2'>
													<td>{finalTotalValues.TOTAL_HT} DT</td>
													<td>{finalTotalValues.TOTAL_REMISE} DT</td>
													<td>0.000</td>
													<td>{finalTotalValues.TOTAL_TCC - timbreTotal} DT</td>
												</tr>
												<tr className='bt-tl-2'><td></td><td></td><td></td><td></td></tr>
												<tr className='bt-tl-2'><td></td><td></td><td></td><td></td></tr>
											</tbody>
										</table>
									</td>
									<td>
										<table id='bt-nested-3'>
											<thead><tr id='bt-head-3'><th>NET A PAYER</th></tr></thead>
											<tbody><tr className='bt-tl-3'><td>{totalNetValue} DT</td></tr></tbody>
										</table>
									</td>
								</tr>
								<tr id='secondBigRow'>
									<td><table id='bt-nested-4'><tbody>{totalArr}</tbody></table></td>
									<td><table id='bt-nested-5'><thead><tr id='bt-head-5'><th>CONDITIONS DE REGLEMENT:</th><th></th></tr></thead><tbody><tr className='bt-tl-5'><td></td><td></td></tr><tr className='bt-tl-5'><td>90 JOURS</td><td></td></tr></tbody></table></td>
								</tr>
							</tbody>
						</table>

						<div className='platform-fee-section' style={{ marginTop: '20px', padding: '15px', border: hasCompany() ? '2px solid #FFA000' : '2px solid #4CAF50', borderRadius: '8px', backgroundColor: hasCompany() ? '#FFF8E1' : '#E8F5E9' }}>
							<h3 style={{ color: hasCompany() ? '#D32F2F' : '#2E7D32', marginBottom: '10px', fontSize: '16px' }}>{hasCompany() ? '📢 FRAIS DE PLATEFORME MARKETING (10%)' : '✅ FRAIS DE PLATEFORME MARKETING (0%)'}</h3>
							<table style={{ width: '100%', borderCollapse: 'collapse' }}>
								<thead><tr style={{ borderBottom: '1px solid #ddd' }}><th style={{ textAlign: 'left', padding: '5px', color: hasCompany() ? '#D32F2F' : '#2E7D32' }}>Description</th><th style={{ textAlign: 'right', padding: '5px', color: hasCompany() ? '#D32F2F' : '#2E7D32' }}>Montant</th></tr></thead>
								<tbody>
									<tr><td style={{ textAlign: 'left', padding: '5px' }}>Montant net à payer</td><td style={{ textAlign: 'right', padding: '5px' }}>{totalNetValue} DT</td></tr>
									<tr style={{ borderTop: '2px solid #FFA000' }}><td style={{ textAlign: 'left', padding: '5px', fontWeight: 'bold', color: hasCompany() ? '#D32F2F' : '#2E7D32' }}>Frais marketing (10%)</td><td style={{ textAlign: 'right', padding: '5px', fontWeight: 'bold', color: hasCompany() ? '#D32F2F' : '#2E7D32' }}>{roundToThreeDecimals(platformFee)} DT</td></tr>
								</tbody>
							</table>
						</div>

						{isTunisia && <div className='timbre-info'><p><strong>Note:</strong> Un timbre fiscal de 1 DT par société est appliqué.</p></div>}

						<div className='billEnd'><h2>ARRETER LA PRÉSENTE FACTURE À LA SOMME DE :</h2><span>{convertNumberToWords(parseInt(totalNetValue * 1000))}</span></div>
					</div>
				</div>
			</div>

			<div style={{ display: 'none' }}>
				<PrintToPdf componentRef={componentRef} isTunisia={isTunisia} detailBill={detailBill} billCalculationArr={billCalculationArr} totalNetValue={totalNetValue} totalArr={totalArr} platformFee={platformFee} hasCompany={hasCompany()} adminCompany={detailBill?.adminCompany} />
			</div>
		</div>
	);
};

export default BillDetails;